import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import type { WorkOrder, WorkOrderStatus } from '../../mocks/data/work-orders'
import { SUPPLY_PERSONAS, useSupplyPersonaStore } from '../../store/role'
import { Badge, type BadgeProps } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { AttachmentsPanel } from '../../components/AttachmentsPanel'
import { apiUrl } from '../../lib/api'
import { formatDate, formatDateTime } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { SUPPLY_WORK_ORDERS_STRINGS } from '../../i18n/supply/work-orders'
import { workOrderExtraI18n } from '../../i18n/supply/work-order-extra'
import { WorkOrderOperationsPanels } from './WorkOrderOperationsPanels'
import { WorkOrderPackingSlips } from './WorkOrderPackingSlips'
import { EntityMessagingAction } from '../shared/messaging/EntityMessagingAction'
import { LinkedTasksPanel } from '../staff-tasks/LinkedTasksPanel'
import { formatOrderRef, formatWorkOrderRef } from './workOrderDisplay'

// Phase 1 OM dual-path WO state machine (Volkan ratify 2026-06-03 + memory
// feedback_fulfillment_mode_c_staging_per_product). Both hub_via_nj 5-state
// and direct_dropship 4-state share the first 3 states (assigned →
// acknowledged → in_production); paths diverge on dispatch destination.
// QC removed as state — now in-production event (Phase 4 QCPanel).

// Same token-based badge tones as the work order list (UX-20261101-20).
const STATUS_VARIANT: Record<WorkOrderStatus, NonNullable<BadgeProps['variant']>> = {
  assigned: 'neutral',
  acknowledged: 'info',
  in_production: 'warning',
  dispatched_to_warehouse: 'info',
  received_at_warehouse: 'success',
  dispatched_to_customer: 'success',
  cancelled: 'destructive',
}

const STATE_FLOW_HUB: WorkOrderStatus[] = [
  'assigned',
  'acknowledged',
  'in_production',
  'dispatched_to_warehouse',
  'received_at_warehouse',
]

const STATE_FLOW_DROPSHIP: WorkOrderStatus[] = [
  'assigned',
  'acknowledged',
  'in_production',
  'dispatched_to_customer',
]

function stateFlowFor(mode: 'hub_via_nj' | 'direct_dropship'): WorkOrderStatus[] {
  return mode === 'hub_via_nj' ? STATE_FLOW_HUB : STATE_FLOW_DROPSHIP
}

export function WorkOrderDetail({ id: idProp }: { id?: string } = {}) {
  // Dual-mode (Volkan ratify 2026-06-03 smoke-test #20-25; cross-shell
  // pattern via [[backlog_style_guideline_shell_detail_modal]]):
  // route mode reads from useParams; modal mode receives id via prop.
  const params = useParams({ strict: false }) as { id?: string }
  const id = idProp ?? params.id ?? ''
  const qc = useQueryClient()
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const [note, setNote] = useState('')
  const noteId = useId()
  const t = useT(SUPPLY_WORK_ORDERS_STRINGS).workOrderDetail
  const tx = useT(workOrderExtraI18n)

  const statusLabelOf = (state: WorkOrderStatus): string => {
    switch (state) {
      case 'assigned':
        return t.statusLabel.assigned
      case 'acknowledged':
        return t.statusLabel.acknowledged
      case 'in_production':
        return t.statusLabel.inProduction
      case 'dispatched_to_warehouse':
        return t.statusLabel.dispatchedToWarehouse
      case 'received_at_warehouse':
        return t.statusLabel.receivedAtWarehouse
      case 'dispatched_to_customer':
        return t.statusLabel.dispatchedToCustomer
      case 'cancelled':
        return t.statusLabel.cancelled
    }
  }

  const nextLabelOf = (state: WorkOrderStatus): string => {
    switch (state) {
      case 'assigned':
        return t.nextActionLabel.acknowledge
      case 'acknowledged':
        return t.nextActionLabel.startProduction
      case 'in_production':
        return t.nextActionLabel.dispatch
      case 'dispatched_to_warehouse':
        return t.nextActionLabel.markReceived
      case 'dispatched_to_customer':
      case 'received_at_warehouse':
      case 'cancelled':
        return t.nextActionLabel.terminal
    }
  }

  const { data, isLoading, isError, error } = useQuery<WorkOrder>({
    queryKey: ['supply', 'work-orders', persona, id],
    queryFn: async () => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${id}?persona=${persona}`),
      )
      if (!res.ok) {
        const p = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
        throw new Error(p.detail ?? `HTTP ${res.status}`)
      }
      return res.json()
    },
  })

  const transitionMut = useMutation({
    mutationFn: async (noteText: string) => {
      const res = await fetch(
        apiUrl(`/supply/my-work-orders/${id}/transition?persona=${persona}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: noteText }),
        },
      )
      if (!res.ok) {
        const p = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
        throw new Error(p.detail ?? `HTTP ${res.status}`)
      }
      return res.json() as Promise<WorkOrder>
    },
    onSuccess: (wo) => {
      setNote('')
      qc.invalidateQueries({ queryKey: ['supply', 'work-orders'] })
      qc.invalidateQueries({ queryKey: ['admin', 'activity'] })
      qc.invalidateQueries({ queryKey: ['admin', 'metrics'] })
      qc.invalidateQueries({ queryKey: ['alerts'] })
      toast.success(t.toasts.transitionSuccessTitle(statusLabelOf(wo.state)), {
        description: t.toasts.transitionSuccessDesc(wo.product_name, wo.quantity),
        audience: 'supply',
      })
      toast.info(t.toasts.transitionAdminTitle, {
        description: t.toasts.transitionAdminDesc(
          wo.assigned_to_org_name,
          wo.product_name,
          statusLabelOf(wo.state),
        ),
        audience: 'admin',
      })
    },
    onError: (err) => {
      toast.error(t.toasts.transitionErrorTitle, {
        description: err instanceof Error ? err.message : t.unknownError,
        audience: 'supply',
      })
    },
  })

  if (info.org_type !== 'SO2') {
    return (
      <div className="max-w-2xl rounded-lg border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-4 py-6 text-sm text-[var(--color-text-muted)]">
        {t.nonSo2Gate(info.org_name, info.org_type)}
      </div>
    )
  }
  if (isLoading) return <div className="text-sm text-[var(--color-text-muted)]">{t.loading}</div>
  if (isError || !data) {
    return (
      <div className="text-sm text-[var(--color-danger)]">
        {t.errorPrefix} {error instanceof Error ? error.message : t.unknownError}
        <div className="mt-3">
          <Link to="/supply/work-orders" className="text-[var(--color-info)] underline">
            {t.listLink}
          </Link>
        </div>
      </div>
    )
  }

  const stateFlow = stateFlowFor(data.fulfillment_mode)
  const currentIdx = stateFlow.indexOf(data.state)
  const isTerminal =
    data.state === 'received_at_warehouse' ||
    data.state === 'dispatched_to_customer'

  return (
    <div className="w-full">
      <div className="text-sm">
        <Link to="/supply/work-orders" className="text-[var(--color-info)] hover:underline">
          {t.backLink}
        </Link>
      </div>

      <header className="mt-3 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-text)]">{data.product_name}</h1>
          <div className="mt-1 text-sm text-[var(--color-text-muted)]">
            <span className="font-mono" title={data.id}>
              {formatWorkOrderRef(data.id)}
            </span>
            <span className="mx-2">·</span>
            <span>{data.product_id}</span>
            <span className="mx-2">·</span>
            <span className="tabular-nums">{t.headerLine.qtyUnits(data.quantity)}</span>
            <span className="mx-2">·</span>
            <span>
              {t.headerLine.customerRefLabel}{' '}
              <span className="font-mono" title={data.customer_order_ref}>
                {formatOrderRef(data.customer_order_ref)}
              </span>
            </span>
          </div>
        </div>
        <Badge variant={STATUS_VARIANT[data.state]}>{statusLabelOf(data.state)}</Badge>
      </header>

      <div className="mt-3">
        <EntityMessagingAction
          surface="supply"
          entityType="work_order"
          entityId={data.id}
          topic="production"
        />
      </div>

      <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.panels.summary}
        </h2>
        <div className="mt-2 space-y-1 text-sm text-[var(--color-text)]">
          <div>
            <span className="font-medium text-[var(--color-text)]">{t.summaryFields.assignedAtLabel}</span>{' '}
            {formatDateTime(data.assigned_at)}
          </div>
          <div>
            <span className="font-medium text-[var(--color-text)]">{t.summaryFields.dueLabel}</span>{' '}
            {formatDate(data.due_date)}
          </div>
          {data.notes && (
            <div>
              <span className="font-medium text-[var(--color-text)]">{t.summaryFields.notesLabel}</span>{' '}
              <span className="italic">{data.notes}</span>
            </div>
          )}
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.panels.stateMachine}
        </h2>
        {/* State machine visual hierarchy (smoke-test #26-30 2026-06-04
            Volkan feedback: "hangisi aktif belli olmuyor"):
              Past     = muted slate + checkmark icon
              Current  = strong amber + bold + ring + pulse
              Future   = light slate + dashed border
            Connector arrows: emerald past, slate future. */}
        <div className="mt-3 flex items-center gap-1.5 overflow-x-auto">
          {stateFlow.map((s, i) => {
            const isCurrent = i === currentIdx
            const isPast = i < currentIdx
            return (
              <div key={s} className="flex items-center gap-1.5 shrink-0">
                <div
                  aria-current={isCurrent ? 'step' : undefined}
                  className={
                    'rounded-md px-2.5 py-1 text-xs whitespace-nowrap inline-flex items-center gap-1 ' +
                    (isCurrent
                      ? 'bg-[var(--color-action)] text-[var(--color-action-foreground)] font-bold ring-2 ring-[var(--color-border-strong)] shadow-sm'
                      : isPast
                        ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] font-normal'
                        : 'border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text-muted)] font-normal')
                  }
                >
                  {isPast && (
                    <span className="text-[var(--color-success)]" aria-hidden="true">
                      ✓
                    </span>
                  )}
                  {isCurrent && (
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-action-foreground)] animate-pulse"
                      aria-hidden="true"
                    />
                  )}
                  {statusLabelOf(s)}
                </div>
                {i < stateFlow.length - 1 && (
                  <span className={isPast ? 'text-[var(--color-success)]' : 'text-[var(--color-text-muted)]'}>
                    →
                  </span>
                )}
              </div>
            )
          })}
        </div>

        {/* in_production ships ONLY through the Dispatch panel below (carrier +
            tracking required) — the plain "next step" form would skip both
            (UX-20261031-11). */}
        {data.state === 'in_production' && (
          <p className="mt-4 text-sm text-[var(--color-text-muted)]">
            {t.transitionForm.dispatchViaPanel}
          </p>
        )}
        {!isTerminal && data.state !== 'in_production' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              transitionMut.mutate(note)
            }}
            className="mt-4 space-y-2"
          >
            <label htmlFor={noteId} className="block text-sm font-medium text-[var(--color-text)]">
              {t.transitionForm.noteLabel}
            </label>
            <textarea
              id={noteId}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder={t.transitionForm.notePlaceholder}
              className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-focus)] focus:outline-none focus-visible:shadow-[var(--shadow-focus)]"
            />
            <Button type="submit" disabled={transitionMut.isPending}>
              {transitionMut.isPending
                ? t.transitionForm.transitionPending
                : nextLabelOf(data.state)}
            </Button>
            {transitionMut.error && (
              <div className="text-sm text-[var(--color-danger)]">
                {t.transitionForm.errorPrefix}{' '}
                {transitionMut.error instanceof Error
                  ? transitionMut.error.message
                  : t.unknownError}
              </div>
            )}
          </form>
        )}
        {isTerminal && (
          <div className="mt-3 text-sm text-[var(--color-text-muted)]">
            {t.terminalNote}
          </div>
        )}
      </section>

      {/* Phase 4 OM operations panels — per-state action surfaces. State
          machine still drives the simple "next transition" form above; the
          panels below add structured QC + dispatch + receive flows that
          carry meta (carrier/tracking/scan_token) audit trail. */}
      <WorkOrderOperationsPanels wo={data} persona={persona} />

      {/* Phase 4 OM dual packing slips — persona-filtered S8 (Aybars sees
          full + producer sees minimal; direct_dropship exception per
          memory). */}
      <WorkOrderPackingSlips woId={data.id} persona={persona} />

      {/* Proof-of-work photos (production step / QC / packaging / damage) and
          the design source / mockup / production-final documents both attach
          to component='work_order' — filterRole keeps them as two lists
          instead of each panel showing the other's uploads. 'gallery' (not
          'photo') because DriveFileRole has no 'photo' value — the real
          confirm endpoint 422s on an unsupported role. */}
      <div className="mt-6">
        <AttachmentsPanel
          component="work_order"
          objectId={data.id}
          title={tx.productionPhotos}
          role="gallery"
          filterRole="gallery"
        />
      </div>

      {/* Phase 3.4 production files: design source / mockup / production-final
          with approve+lock + a public-link QR for the floor (component='work_order'
          → producer's own org). Distinct from the proof-of-work photos above. */}
      <div className="mt-6">
        <AttachmentsPanel
          component="work_order"
          objectId={data.id}
          title={tx.productionFiles}
          filterRole="document"
          lifecycle
        />
      </div>

      <section className="mt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          {t.panels.lifecycleHistory}
        </h2>
        <div className="mt-2 space-y-2">
          {(data.transitions ?? []).length === 0 && (
            <div className="rounded-lg border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-text-muted)]">
              {t.historyEmpty}
            </div>
          )}
          {(data.transitions ?? []).map((tr, i) => (
            <div key={i} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
              <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                <span className="font-medium text-[var(--color-text)]">{tr.by_persona}</span>
                <span>{formatDateTime(tr.at)}</span>
              </div>
              <p className="mt-1 text-sm text-[var(--color-text)]">
                <span className="text-xs text-[var(--color-text-muted)]">
                  {tr.from_state
                    ? `${statusLabelOf(tr.from_state) || tr.from_state} → `
                    : ''}
                  {statusLabelOf(tr.to_state) || tr.to_state}
                </span>
                {tr.note && <span className="ml-2">· {tr.note}</span>}
              </p>
            </div>
          ))}
        </div>
      </section>

      <LinkedTasksPanel
        shell="supply"
        className="mt-6"
        link={{ kind: 'work_order', id: data.id, label: data.product_name.slice(0, 200) }}
      />
    </div>
  )
}
